<template>
  <div class="container-fluid p-3">
    <div class="d-flex flex-row justify-content-between">
      <div class="d-flex flex-column">
        <div class="title d-flex align-items-center">
          <inline-svg class="me-2" width="36" height="36" src="/assets/img/icons/globe-01.svg"></inline-svg>
          Community Library
        </div>
        <div class="subtitle">Card Decks created by other Caregivers.</div>
      </div>
    </div>
    <div class="d-flex flex-row mt-3 align-items-center justify-content-between">
      <div class="d-flex align-items-center w-50">
        <form-input class="me-2 w-100" style="min-width: 320px" v-model="filters.query"
                    placeholder="Search in community" @keypress.enter="search">
          <template slot="prepend">
            <inline-svg src="/assets/img/icons/search-icon.svg"></inline-svg>
          </template>
        </form-input>
      </div>
      <div class="d-flex flex-row align-items-center me-2">
        <span>Sort by</span>
        <form-select class="ms-1" style="margin-right: 20px" v-model="filters.status" @change="search"
                     :options="[
                        {'label': '', 'value': null},
                        {'label': 'Trending', 'value': 'trending'},
                        {'label': 'Popular', 'value': 'popular'},
                        {'label': 'Recent', 'value': 'recent'},
                    ]"
        />
      </div>
      <div>
        <btn-group-toggle :options="[
                    {'label': 'Partners', 'value': 'partner_decks'},
                    {'label': 'Community', 'value': 'community_decks'}
                    ]" v-model="filters.filter_by" @input="getData">
        </btn-group-toggle>
      </div>
      <btn class="btn-light-primary ms-1" @click="$router.push({name: 'card.deck.favorites'})">
        <inline-svg width="16" class="me-1" style="min-width: 16px"
                    src="/assets/img/icons/heart-circle.svg"></inline-svg>
        Favorites
      </btn>
    </div>
    <div class="deck-card-list mt-2" :class="{loading: loading.data}">
      <template v-for="cardDeck in cardDecks">
        <deck-card-partner v-if="cardDeck.corporateBrand"
                           :cardDeck="cardDeck"
                           :communityCard="true">
        </deck-card-partner>
        <deck-card-regular v-else
                           :cardDeck="cardDeck"
                           :communityCard="true">
        </deck-card-regular>
      </template>
    </div>
    <div v-if="showLoadMore && !loading.data" class="flex-1 text-center">
      <btn class="btn-outline-light" @click="loadMore">
        Load more
        <inline-svg class="ms-1" src="/assets/img/icons/arrow-down.svg"></inline-svg>
      </btn>
    </div>
    <modal name="confirmFavoriteCardDeckAddedModal" height="auto" width="500px" :scrollable="true">
      <div class="modal-content">
        <div class="modal-header d-flex flex-column">
          <div class="w-100 text-center">
            <img class="mb-2 img-fluid" src="/assets/img/png/favorites-screenshot.png" alt="favorites"/>
          </div>
          <h4 class="modal-title">Favourite Card Deck Added</h4>
          <div class="modal-subtitle-gray text-center">The heart button allows you to mark this Card Deck as a favorite.
            You can view all your favorites in the Favorites section the left side menu.<br><br>
            You can also share your favorites with Heroes, caregivers, or professionals.
          </div>
        </div>
        <div class="d-flex flex-row gap-2 p-3 mt-2 justify-content-center align-items-center">
          <form-checkbox
            v-model="hideConfirmFavoriteCardDeckAddedModal"
            @changed="doNotShowAddFavoritesConfirmationModal"
            text="Don't show again"
          />
          <div class="d-flex flex-1 flex-row justify-content-between gap-2">
            <btn class="btn-outline-light btn-lg flex-1"
                 @click="$router.push({name: 'card.deck.favorites'})">
              Go to Favorites
            </btn>
            <btn class="btn-primary btn-lg flex-1"
                 @click="$modal.hide('confirmFavoriteCardDeckAddedModal')">
              Got it
            </btn>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>

import { modelListMixin } from "/src/mixins/modelListMixin.js";
import DeckCardPartner from "@/components/DeckCardPartner.vue";
import DeckCardRegular from "@/components/DeckCardRegular.vue";
import BtnGroupToggle from "@/components/core/BtnGroupToggle.vue";
import { mapState } from "vuex";

export default {
  name: "CommunityLibrary",
  components: { BtnGroupToggle, DeckCardPartner, DeckCardRegular },
  mixins: [modelListMixin],
  data() {
    return {
      cardDecks: [],
      filters: {
        filter_by: "partner_decks"
      },
      showLoadMore: true,
      hideConfirmFavoriteCardDeckAddedModal: false,
    };
  },
  watch: {
    'filters.query': function(newVal, oldVal) {
      if (newVal !== '') {
        this.$gtag.event('community_search_performed', {
          search_term: newVal,
          filters_used: [this.filters.filter_by, this.filters.status],
        });
      }
    }
  },
  mounted() {
    this.hideConfirmFavoriteCardDeckAddedModal = localStorage.getItem("hideConfirmFavoriteCardDeckAddedModal") === "true";
    this.getData();
    this.$events.$on("loadCommunityCardDecks", () => {
      this.getData();
    });
    this.$gtag.event('community_library_viewed', {
      user_id: this.user.uid,
    });
    this.startTracking();
    window.addEventListener('beforeunload', this.handleUnload);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeDestroy() {
    this.stopTrackingAndSendEvent();
    window.removeEventListener('beforeunload', this.handleUnload);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },
  computed: {
    ...mapState({
      user: state => state.user,
    }),
  },
  methods: {
    getData() {
      this.loading.data = true;
      this.$http.post("/deck/community-decks", this.filters)
        .then((res) => {
          this.showLoadMore = res.body.data.current_page !== res.body.data.last_page;
          this.cardDecks = this.filters.page === 1 ? res.body.data.data : this.cardDecks.concat(res.body.data.data);
          this.loading.data = false;
        })
        .catch((error) => {
          this.loading.data = false;
        });
    },
    loadMore() {
      this.filters.page++;
      this.getData();
    },
    doNotShowAddFavoritesConfirmationModal() {
      if (this.hideConfirmFavoriteCardDeckAddedModal) {
        localStorage.setItem('hideConfirmFavoriteCardDeckAddedModal', 'true');
      } else {
        localStorage.setItem('hideConfirmFavoriteCardDeckAddedModal', 'false');
      }
    },
    startTracking() {
      this.trackingStartTime = Date.now();
    },
    stopTrackingAndSendEvent() {
      const endTime = Date.now();
      const duration = Math.round((Date.now() - this.trackingStartTime) / 1000);
      this.$gtag.event('time_spent_in_library', {
        tracked_user_id: this.user.uid,
        time_spent: duration
      });
    },
    handleUnload() {
      const duration = Math.round((Date.now() - this.trackingStartTime) / 1000);
      const payload = {
        event: 'time_spent_in_library',
        duration_seconds: duration,
        tracked_user_id: this.user.uid,
      };

      const blob = new Blob([JSON.stringify(payload)], {
        type: 'application/json'
      });

      navigator.sendBeacon('https://api.magnusmode.com/api/caregiver/analytics/track-event', blob);
    }
  }
};
</script>
